<template>
  <v-app>
    <div class="maintenanceList">
      <v-tabs
        v-model="tab"
        grow
        centered
        icons-and-text
      >
        <v-tab href="#tab-1">
          <p>New Requests</p>
          <v-icon>mdi-new-box</v-icon>
        </v-tab>

        <v-tab href="#tab-2">
          <p>Assigned Requests</p>
          <v-icon>mdi-progress-clock</v-icon>
        </v-tab>

        <v-tab href="#tab-3">
          <p>Closed Requests</p>
          <v-icon>mdi-chart-box-multiple-outline</v-icon>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card>
            <v-card-title>
              New Requests
              <v-spacer></v-spacer>
              <div class="d-flex justify-content-end align-items-center gap-3" style="gap: 10px;">

                  <v-text-field
                    v-model="search"
                    class="mr-3"
                    append-icon="mdi-magnify"
                    :label="$t('GeneralTranslations.Search')"
                    single-line
                    hide-details
                    style="min-width: 300px"
                  ></v-text-field>

                  <v-dialog v-model="dialog" max-width="700px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn color="primary" v-bind="attrs" v-on="on">New +</v-btn>
                    </template>

                    <v-card>
                      <validation-observer ref="form" v-slot="{ invalid }">
                        <v-form ref="createMaintenanceRequestForm" lazy-validation @submit.prevent="submit">
                          <v-card-title>
                            <span class="headline">Create Maintenance Request</span>
                          </v-card-title>
                          <v-card-text>
                            <v-container>
                              <v-row align="center">
                                <v-col cols="12">
                                  <validation-provider v-slot="{ errors }" name="RequestType" rules="required">
                                    <v-autocomplete
                                      v-model="reqType"
                                      :items="maintenanceTypes"
                                      density="compact"
                                      variant="solo"
                                      item-text="name"
                                      return-object
                                      label="Request Type*"
                                      :error-messages="errors"
                                      required
                                    ></v-autocomplete>
                                  </validation-provider>
                                </v-col>

                                <v-col cols="6">
                                  <v-textarea
                                    v-model="newMaintenanceRequest.description"
                                    label="Request Description"
                                    rows="2"
                                    hint="Provide more information on the specific location or details related to the issue."
                                  ></v-textarea>
                                </v-col>

                                <v-col cols="6">
                                  <v-textarea
                                    v-model="newMaintenanceRequest.comment"
                                    label="Comment"
                                    rows="2"
                                  ></v-textarea>
                                </v-col>

                                <v-col cols="12" class="pt-0 pb-0">
                                  <small>Selecting a resident is optional but useful if you want to link this ticket to resident and their room.</small>
                                </v-col>
                                <v-col cols="6">
                                  <v-autocomplete
                                    v-model="newMaintRes"
                                    :items="residentsList"
                                    density="compact"
                                    variant="solo"
                                    item-text="name"
                                    return-object
                                    label="Resident"
                                    clearable
                                    @click:clear="clearRes()"
                                  ></v-autocomplete>
                                </v-col>
                                <v-col cols="6">
                                  <v-text-field
                                    v-model="newMaintenanceRequest.roomName"
                                    label="Room"
                                    :disabled="newMaintRes != null"
                                    required
                                  ></v-text-field>
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="6" class="pb-0">
                                  <v-autocomplete
                                    :items="eventLocations"
                                    item-text="name"
                                    item-value="id"
                                    v-model.trim="newMaintenanceRequest.locationId"
                                    :label="`${$t('NewCalendarItem.Location')}`"
                                    :search-input.sync="newLocationName"
                                    ref="location"
                                    class="maintenance-location"
                                  >
                                    <template v-if="!eventLocations.find(x => x.name === newLocationName)" #append-item="">
                                      <v-btn
                                        class="ml-2 mt-2"
                                        color="primary"
                                        width="96%"
                                        @click="addNewLocation()"
                                        :disabled="isDisable || !newLocationName"
                                      >{{ $t('NewCalendarItem.AddNewLocation') }}</v-btn>
                                    </template>
                                  </v-autocomplete>
                                </v-col>
                                <v-col cols="6" class="pb-0">
                                  <v-autocomplete
                                    class="select-wings"
                                    v-model="newMaintenanceRequest.wingId"
                                    :items="wings"
                                    :label="$t('NewCalendarItem.Wing')"
                                    ref="wings"
                                    item-text="name"
                                    item-value="id"
                                    @click:append-outer="openAllWings"
                                    :search-input.sync="newWingName"
                                  >
                                    <template v-if="!wings.find(x => x.name === newWingName) && isStaffCanCreateWings" #append-item="">
                                      <v-btn
                                        class="ml-2 mt-2"
                                        color="primary"
                                        width="94%"
                                        @click="addNewWing()"
                                        :disabled="isDisableWing || !newWingName"
                                      >{{ $t('AdmitResidentsDialog.AddNewWing') }}</v-btn>
                                    </template>
                                  </v-autocomplete>
                                </v-col>
                                <v-col cols="12" class="email-request-to-vendor">
                                  <v-checkbox
                                    v-model="newMaintenanceRequest.isSendEmailToVendor"
                                    :label="$t('MaintenanceSchedule.EmailRequestToVendor')"
                                  ></v-checkbox>
                                </v-col>
                                <v-col cols="12">
                                  <small>Additional location based information.</small>
                                </v-col>
                              </v-row>
                            </v-container>
                            <small class="pl-3">{{ $t('GeneralTranslations.StarIndicatesRequiredField') }}</small>
                          </v-card-text>

                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn outlined color="primary" @click="close">{{ $t('GeneralTranslations.Cancel') }}</v-btn>

                            <v-btn type="submit" color="primary" :disabled="invalid">
                              {{ $t("GeneralTranslations.Create") }}
                            </v-btn>
                          </v-card-actions>
                        </v-form>
                      </validation-observer>
                    </v-card>
                  </v-dialog>

                  <export-excel
                    :data="mergeAllRequests"
                    :fields="allRequestsJsonFields"
                    class="btn btn-default"
                    style="padding: 0; border: 0;"
                    name="allServiceRequests.csv"
                    type="csv"
                  >
                    <v-btn class="primary white--text">{{ $t('GeneralTranslations.Export') }}</v-btn>
                  </export-excel>

                  <v-btn color="primary" @click.native="openQRCode">
                    Open QR Code
                  </v-btn>
              </div>
            </v-card-title>
            <v-data-table
              :loading="loading"
              :no-data-text="$t('MaintenanceList.NoDataText')"
              :headers="headers"
              :items="maintenanceRequestsNew"
              :search="search"
              @click:row="handleClickMaintenanceRequest"
            >
              <template v-slot:item.friendlyText="{ item }">
                <span v-if="item.friendlyText">{{
                  $t(
                    `Maintenance.CategoryText.${convertToTranslationKey(
                      item.friendlyText
                    )}`
                  )
                }}</span>
                <span v-if="item.name">{{ item.name }}</span>
              </template>

              <template v-slot:item.category="{ item }">
                <span v-if="checkExistCategory(item.category)">{{
                  $t(
                    `Maintenance.Category.${convertToTranslationKey(
                      item.category
                    )}`
                  )
                }}</span>
                <span v-else>{{ item.category }}</span>
              </template>

              <template v-slot:item.icon="{ item }">
                <v-icon
                  large
                  color="primary"
                  >{{ getIcon(item.icon) }}</v-icon
                >
              </template>

              <template v-slot:item.roomName="{ item }">
                <span>{{ (item.roomName ? item.roomName : '-') + ' / ' + (item.location ? item.location : '-')}}</span>
              </template>

              <template v-slot:item.requestAge="{ item }">
                <span :class="item.requestAgeColor">
                  <b>{{ item?.hoursAgo }}</b>
                </span>
              </template>

              <template v-slot:item.dueDate="{ item }">
                <span :class="item.dueDate">
                  <b>{{ item?.dueDate }}</b>
                </span>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

        <v-tab-item value="tab-2">
          <v-card>
            <v-card-title>
              Assigned Requests
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                :label="$t('GeneralTranslations.Search')"
                single-line
                hide-details
              ></v-text-field>
            </v-card-title>
            <v-data-table
              :loading="loading"
              :no-data-text="$t('MaintenanceList.NoDataText')"
              :headers="headersInProgress"
              :items="maintenanceRequestsInProgress"
              :search="search"
              @click:row="handleClickMaintenanceRequest"
            >
              <template v-slot:item.friendlyText="{ item }">
                <span v-if="item.friendlyText">{{
                  $t(
                    `Maintenance.CategoryText.${convertToTranslationKey(
                      item.friendlyText
                    )}`
                  )
                }}</span>
                <span v-if="item.name">{{ item.name }}</span>
              </template>

              <template v-slot:item.category="{ item }">
                <span v-if="checkExistCategory(item.category)">{{
                  $t(
                    `Maintenance.Category.${convertToTranslationKey(
                      item.category
                    )}`
                  )
                }}</span>
                <span v-else>{{ item.category }}</span>
              </template>

              <template v-slot:item.status="{ item }">
                <span>{{ getStatus(item.status) }}</span>
              </template>

              <template v-slot:item.icon="{ item }">
                <v-icon large color="primary">
                  {{ getIcon(item.icon) }}
                </v-icon>
              </template>
              
              <template v-slot:item.roomName="{ item }">
                <span>{{ item.roomName + " " + item.location }}</span
                >
              </template>

              <template v-slot:item.requestAge="{ item }">
                <span :class="item.requestAgeColor">
                  <b>{{ item?.hoursAgo }}</b>
                </span>
              </template>

              <template v-slot:item.priority="{ item }">
                <span :class="item.priorityColor">
                  <b>{{ item?.priority }}</b>
                </span>
              </template>

              <template v-slot:item.dueDate="{ item }">
                <span :class="item.dueDate">
                  <b>{{ item?.dueDate }}</b>
                </span>
              </template>

              <template v-slot:item.estimatedCompletionDate="{ item }">
                <span :class="item.estimatedCompletionDate">
                  <b>{{ item?.estimatedCompletionDate | localizedDate(facility.dateFormat)}}</b>
                </span>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

        <v-tab-item value="tab-3">
          <v-card>
            <v-card-title>
              Closed Requests
              <v-spacer></v-spacer>
            </v-card-title>

            <v-flex
              lg12
              md12
              xs12
            >
              <v-row class="mh-fix">
                <v-col cols="2">
                  <v-menu
                    v-model="picker.start"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedStartDate"
                        :label="$t('GeneralTranslations.StartDate')"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      :locale="$i18n.locale"
                      v-model="startDate"
                      @input="picker.start = false"
                    >
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="2">
                  <v-menu
                    v-model="picker.end"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedEndDate"
                        :label="$t('GeneralTranslations.EndDate')"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      >
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :locale="$i18n.locale"
                      v-model="endDate"
                      @input="picker.end = false"
                    >
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="1">
                  <v-btn
                    class="primary"
                    @click="loadClosedRequests"
                    :disabled="loading"
                    >{{ $t('GeneralTranslations.Update') }}</v-btn
                  >
                </v-col>
                <v-col cols="1">
                  <export-excel
                    :data="closedMaintenanceRequests"
                    :fields="jsonFields"
                    name="Closed Maintenance Requests Report.csv"
                    type="csv"
                  >
                    <v-btn class="primary">{{
                      $t('GeneralTranslations.Export')
                    }}</v-btn>
                  </export-excel>
                </v-col>
              </v-row>
            </v-flex>

            <v-flex
              lg12
              md12
              xs12
            >
              <v-row class="">
                <v-card class="m-2 ml-4 mt-4 mb-4">
                  <v-card-title>
                    Requests
                    <v-spacer></v-spacer>
                    <v-icon>mdi-spanner</v-icon>
                  </v-card-title>

                  <v-card-text class="py-0 pb-10">
                    <v-row
                      align="center"
                      no-gutters
                    >
                      <v-col
                        align="center"
                        cols="12"
                      >
                        <v-col cols="12">
                          <span class="text-h2">{{closedMaintenanceRequests?.length ?? 0 }}</span>
                        </v-col>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-card class="m-2 mt-4 mb-4">
                  <v-card-title>
                    Priority
                    <v-spacer></v-spacer>
                    <v-icon>mdi-spanner</v-icon>
                  </v-card-title>

                  <v-card-text class="py-0">
                    <v-row
                      align="center"
                      no-gutters
                    >
                      <v-col
                        align="center"
                        cols="12"
                      >
                        <pie-chart
                          :data="closedByPriority"
                          height="160px"
                          :donut="true"
                          :colors="['#ff8080', '#CDF69A', '#FFBD31', '#ff8080']"
                        ></pie-chart
                      ></v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-card class="m-2 mt-4 mb-4">
                  <v-card-title>
                    Category
                    <v-spacer></v-spacer>
                    <v-icon>mdi-spanner</v-icon>
                  </v-card-title>

                  <v-card-text class="py-0">
                    <v-row
                      align="center"
                      no-gutters
                    >
                      <v-col
                        align="center"
                        cols="12"
                      >
                        <column-chart
                          :data="closedByCategory"
                          :colors="['1948b3']"
                          height="180px"
                        ></column-chart
                      ></v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-card class="ml-6 mr-5 mt-4 mb-4">
                  <v-card-title>
                    Avg. Time to Close
                  </v-card-title>

                  <v-card-text class="py-0 pb-5">
                    <v-row
                      align="center"
                      no-gutters
                    >
                      <v-col
                        align="center"
                        cols="12"
                      >
                        <v-col cols="12">
                          <span class="text-h3">
                            <span v-if="closedMaintenanceRequests.length">{{Math.round(closedMaintenanceRequests.reduce((a, b) => a + b.openDuration, 0) / closedMaintenanceRequests.length)}}</span>
                            <span v-else>0</span>
                            days
                          </span>
                        </v-col>
                      </v-col>
                    </v-row>
                    
                  </v-card-text>
                </v-card>
              </v-row>
            </v-flex>

            <v-flex
              lg12
              md12
              xs12
            >
              <v-data-table
                :loading="loading"
                :no-data-text="$t('MaintenanceList.NoDataText')"
                :headers="headersClosed"
                :items="closedMaintenanceRequests"
                :search="search"
                @click:row="handleClickMaintenanceRequest"
              >
                <template v-slot:item.friendlyText="{ item }">
                  <span v-if="item.friendlyText">{{
                    $t(
                      `Maintenance.CategoryText.${convertToTranslationKey(
                        item.friendlyText
                      )}`
                    )
                  }}</span>
                  <span v-if="item.name">{{ item.name }}</span>
                </template>

                <template v-slot:item.category="{ item }">
                  <span v-if="checkExistCategory(item.category)">{{
                    $t(
                      `Maintenance.Category.${convertToTranslationKey(
                        item.category
                      )}`
                    )
                  }}</span>
                  <span v-else>{{ item.category }}</span>
                </template>

                <template v-slot:item.status="{ item }">
                  <span>{{ getStatus(item.status) }}</span>
                </template>

                <template v-slot:item.icon="{ item }">
                  <v-icon
                    large
                    color="primary"
                    >{{ getIcon(item.icon) }}</v-icon
                  >
                </template>

                <template v-slot:item.requestAge="{ item }">
                  <span :class="item.requestAgeColor">
                    <b>{{ item?.hoursAgo }}</b>
                  </span>
                </template>

                <template v-slot:item.priority="{ item }">
                  <span :class="item.priorityColor">
                    <b>{{ item?.priority }}</b>
                  </span>
                </template>

                <template v-slot:item.dueDate="{ item }">
                  <span :class="item.dueDate">
                    <b>{{ item?.dueDate }}</b>
                  </span>
                </template>
              </v-data-table>
            </v-flex>
          </v-card>
        </v-tab-item>
      </v-tabs-items>

      <MaintenanceQRCodeDlg ref="maintenanceQRCodeDlg"/>
    </div>
  </v-app>
</template>

<script>
import { toTranslationKey } from '@/utils/stringFormat';
import { maintenanceCategoriesKeys } from '@/consts/MaintenanceCategoriesKeys';
import { MaintenanceMixin } from '@/mixins/Maintenance';
import { EventLocationsMixin } from "@/mixins/EventLocations";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import MaintenanceQRCodeDlg from "@/views/maintenance/maintenanceQRCodeDlg/maintenanceQRCodeDlg.vue";
import router from '@/router';
import moment from 'moment';
import {MaintenanceRequestStatus} from "@/enums/MaintenanceRequestStatus";
import {localizedDate} from "@/utils/localizedDate";
import {ResidentStatus} from "@/enums/ResidentStatus";

export default {
  name: 'MaintenanceRequestList',
  components: {
    MaintenanceQRCodeDlg,
    ValidationObserver,
    ValidationProvider
  },
  mixins: [MaintenanceMixin, EventLocationsMixin],
  async created() {
    this.loading = true;
    await this.$store.dispatch('getMaintenanceRequests');
    await this.$store.dispatch('getResidentsShortData');
    await this.$store.dispatch('getMaintenanceTypes');
    await this.loadClosedRequests();
    await this.$store.dispatch('getAllClosedMaintenanceRequests');
    this.loading = false;

    if(this.$route.query?.tab) {
      this.tab = `tab-${this.$route.query?.tab}`;
    }
  },
  data() {
    return {
      loading: true,
      search: '',
      newMaintenanceRequest: {},
      newMaintRes: null,
      reqType: null,
      tab: null,
      dialog: false,
      startDate: moment().subtract(1, 'week').toISOString().substring(0, 10),
      endDate: moment().toISOString().substring(0, 10),
      maintenanceRequestsCompleted: [],
      picker: {
        start: false,
        end: false,
      },
      headers: [
        {
          text: '',
          align: '',
          sortable: false,
          width: 70,
          value: 'icon',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Resident'
          ],
          align: 'start',
          value: 'residentName',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'MaintenanceList.Request'
          ],
          align: 'start',
          sortable: false,
          value: 'friendlyText',
        },
        {
          text: 'Room / Location',
          align: 'start',
          value: 'roomName',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Category'
          ],
          value: 'category',
        },

        {
          text: this.$i18n.messages[this.$i18n.locale][
            'MaintenanceList.RequestAge'
          ],
          align: 'start',
          value: 'requestAge',
        },
      ],
      headersInProgress: [
        {
          text: '',
          align: '',
          sortable: false,
          width: 70,
          value: 'icon',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Resident'
          ],
          align: 'start',
          value: 'residentName',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'MaintenanceList.Request'
          ],
          align: 'start',
          sortable: false,
          value: 'friendlyText',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Category'
          ],
          value: 'category',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Status'
          ],
          value: 'status',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'MaintenanceList.RequestAge'
          ],
          align: 'start',
          value: 'requestAge',
        },
        {
          text: 'Priority',
          align: 'start',
          value: 'priority',
        },
        {
          text: 'Assignee',
          align: 'start',
          value: 'assignee',
        },
        {
          text: 'Estimated Completion Date',
          align: 'start',
          value: 'estimatedCompletionDate',
        },
      ],
      headersClosed: [
        {
          text: '',
          align: '',
          sortable: false,
          width: 70,
          value: 'icon',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Resident'
          ],
          align: 'start',
          value: 'residentName',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'MaintenanceList.Request'
          ],
          align: 'start',
          sortable: false,
          value: 'friendlyText',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Category'
          ],
          value: 'category',
        },
        {
          text: this.$i18n.messages[this.$i18n.locale][
            'GeneralTranslations.Status'
          ],
          value: 'status',
        },
        {
          text: 'Open Duration (in days)',
          align: 'start',
          value: 'openDuration',
        },
        {
          text: 'Priority',
          align: 'start',
          value: 'priority',
        },
        {
          text: 'Assignee',
          align: 'start',
          value: 'assignee',
        },
      ],
      jsonFields: {
        Request: 'name',
        'Room Name': 'roomName',
        Location: 'location',
        AssignedDate: 'assignedDate',
        Category: 'category',
        Type: 'serviceItemType',
        Status: 'status',
        'Open Duration ( In Days)': 'openDuration',
        Assignee: 'assignee',
        Resident: 'residentName',
        'Created Date Time': {
          field: 'dateTime',
          callback: value => {
            const date = moment(value.seconds * 1000);
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Estimated Completion Date': {
          field: 'estimatedCompletionDate',
          callback: value => {
            const date = moment(value, "YYYY-MM-DD");
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Completed Date Time': {
          field: 'completedDateTime',
          callback: value => {
            const date = moment(value.seconds*1000);
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
      },
      allRequestsJsonFields: {
        Request: 'name',
        'Room Name': 'roomName',
        Location: 'location',
        AssignedDate: 'assignedDate',
        Category: 'category',
        Status: 'status',
        'Open Duration ( In Days)': 'openDuration',
        Assignee: 'assignee',
        Resident: 'residentName',
        Priority: 'priority',
        'Request Age': 'hoursAgo',
        'Created Date Time': {
          field: 'dateTime',
          callback: value => {
            const date = moment(value.seconds * 1000);
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Estimated Completion Date': {
          field: 'estimatedCompletionDate',
          callback: value => {
            const date = moment(value, "YYYY-MM-DD");
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
        'Completed Date Time': {
          field: 'completedDateTime',
          callback: value => {
            const date = moment(value.seconds*1000);
            return date.isValid() ? date.format('DD MMM YYYY, HH:mm A') : '';
          },
        },
      }
    };
  },
  computed: {
    maintenanceRequestsInProgress() {
      let maintenanceRequests =
        this.$store.state.maintenanceModule.maintenanceRequests.filter(
          function (el) {
            return el.status == 'inprogress';
          }
        );

      maintenanceRequests.forEach(m => {
        const isHour = m.requestAge >= 60;
        const value = isHour
          ? Number((m.requestAge / 60).toFixed(0))
          : m.requestAge.toFixed(0);
        m.hoursAgo = `${value} ${
          isHour
            ? this.$t(`GeneralTranslations.Hour${value > 1 ? 's' : ''}`)
            : this.$t(`GeneralTranslations.Minute${value > 1 ? 's' : ''}`)
        }`;
        m.requestAgeColor = this.getColor(m.requestAge);

        if (m.priority == 'Urgent' || m.priority == 'High') {
          m.priorityColor = 'red--text';
        } else if (m.priority == 'Medium') {
          m.priorityColor = 'orange--text';
        } else {
          m.priorityColor = 'green--text';
        }
      });

      maintenanceRequests = maintenanceRequests.sort(function (a, b) {
        // Turn your strings into dates, and then subtract them
        // to get a value that is either negative, positive, or zero.
        return (
          new Date(b.estimatedCompletionDate) -
          new Date(a.estimatedCompletionDate)
        );
      });

      return maintenanceRequests;
    },
    maintenanceTypes() {
      return this.$store.state.maintenanceModule.maintenanceTypes;
    },
    closedMaintenanceRequests() {
      return this.$store.state.maintenanceModule.closedMaintenanceRequests;
    },
    allClosedMaintenanceRequests() {
      return this.$store.state.maintenanceModule.allClosedMaintenanceRequests;
    },
    residentsList() {
      return this.$store.state.residentModule.residentsShortData.filter(resident => resident.status !== ResidentStatus.Departed);
    },
    closedByCount() {
      return this.$store.state.maintenanceModule.closedMaintenanceRequests.reduce(
        (acc, req) => {
          if (req?.completedBy) {
            acc[req.completedBy] = acc[req.completedBy] || 0;
            acc[req.completedBy] += 1;
            console.log(req.completedBy);
          }
          return acc;
        },
        {}
      );
    },
    closedByPriority() {
      return this.$store.state.maintenanceModule.closedMaintenanceRequests.reduce(
        (acc, req) => {
          if (req?.priority) {
            acc[req.priority] = acc[req.priority] || 0;
            // The more elegant way: acc[languageId] ??= {name: languageId, count: 0};
            acc[req.priority] += 1;
          }
          return acc;
        },
        {}
      );
    },
    closedByCategory() {
      return this.$store.state.maintenanceModule.closedMaintenanceRequests.reduce(
        (acc, req) => {
          if (req?.category) {
            acc[req.category] = acc[req.category] || 0;
            // The more elegant way: acc[languageId] ??= {name: languageId, count: 0};
            acc[req.category] += 1;
          }
          return acc;
        },
        {}
      );
    },
    maintenanceRequestsNew() {
      const maintenanceRequests =
        this.$store.state.maintenanceModule.maintenanceRequests.filter(
          function (el) {
            return el.status == 'new';
          }
        );

      maintenanceRequests.forEach(m => {
        const isHour = m.requestAge >= 60;
        const value = isHour
          ? Number((m.requestAge / 60).toFixed(0))
          : m.requestAge.toFixed(0);
        m.hoursAgo = `${value} ${
          isHour
            ? this.$t(`GeneralTranslations.Hour${value > 1 ? 's' : ''}`)
            : this.$t(`GeneralTranslations.Minute${value > 1 ? 's' : ''}`)
        }`;
        m.requestAgeColor = this.getColor(m.requestAge);
      });

      return maintenanceRequests;
    },
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    formattedStartDate() {
      return moment(this.startDate).format(this.facility.dateFormat);
    },
    formattedEndDate() {
      return moment(this.endDate).format(this.facility.dateFormat);
    },
    mergeAllRequests() {
      return [...this.maintenanceRequestsNew, ...this.maintenanceRequestsInProgress, ...this.allClosedMaintenanceRequests]
    },
    user() {
      return this.$store.state.authModule.user;
    }
  },
  watch: {
    newMaintRes(res) {
      if(res != null){
      this.newMaintenanceRequest.residentId = res.id;
      this.newMaintenanceRequest.residentName = res.name;
      this.newMaintenanceRequest.roomName = res.roomName;
    }
    },
    reqType(requestType) {
      this.newMaintenanceRequest.name = requestType.name;
      this.newMaintenanceRequest.category = requestType.category;
      this.newMaintenanceRequest.department = requestType.department;
      this.newMaintenanceRequest.typeId = requestType.id;
      this.newMaintenanceRequest.icon = requestType.icon;
    },
    'newMaintenanceRequest.locationId'(locationId, oldlocationId) {
      if(oldlocationId !== locationId) {
        this.newMaintenanceRequest.location = null;
      }

      if(locationId != null) {
        this.newMaintenanceRequest.location = this.eventLocations?.find(x => x.id === locationId)?.name;
      }
    },
  },
  methods: {
    convertToTranslationKey(text) {
      return toTranslationKey(text);
    },
    clearRes() {
      this.newMaintenanceRequest.residentId = null;
      this.newMaintenanceRequest.residentName = null;
      this.newMaintenanceRequest.roomName = null;
      this.newMaintRes = null;
    },
    async loadClosedRequests() {
      await this.$store.dispatch('getClosedMaintenanceRequests', {
        startDate: moment(this.startDate).startOf('day').toDate(),
        endDate: moment(this.endDate).endOf('day').toDate(),
      });
    },
    handleClickMaintenanceRequest(row) {
      router.push('/maintenanceRequests/' + row.id.toString());
    },
    
    async submit() {
      this.newMaintenanceRequest.status = 'new'
      this.newMaintenanceRequest.facilityId = this.facility.id;
      this.newMaintenanceRequest.createdByStaffId = this.user.id;
      this.newMaintenanceRequest.createdByStaffName = this.user.name;

      await this.$store.dispatch('createMaintenanceRequest', this.newMaintenanceRequest)

      this.dialog = false;
    },

    getColor(minutes) {
      if (minutes < 1400) {
        return 'green--text';
      }
      if (minutes < 3000) {
        return 'orange--text';
      }
      return 'red--text';
    },
    checkExistCategory(category) {
      const key = toTranslationKey(category);
      return maintenanceCategoriesKeys.includes(key);
    },
    openQRCode() {
      this.$refs.maintenanceQRCodeDlg.open();
    },
    close() {
      this.newMaintenanceRequest = {};
      this.newMaintRes = null;
      this.reqType = null;
      this.dialog = false;
    },
    getStatus(status) {
      switch (status) {
        case MaintenanceRequestStatus.New: {
          return this.$t("MaintenanceList.New");
        }
        case MaintenanceRequestStatus.InProgress: {
          return this.$t("MaintenanceList.InProgress");
        }
        case MaintenanceRequestStatus.Cancelled: {
          return this.$t("MaintenanceList.Cancelled");
        }
        case MaintenanceRequestStatus.Complete: {
          return this.$t("MaintenanceList.Completed");
        }
      }
    }
  },
};
</script>

<style src="./maintenanceList.scss" lang="scss" scoped></style>